(() => {
  'use strict';

  const roots = Array.from(document.querySelectorAll('[data-nec3d-embed]'));
  if (!roots.length || !window.CHA_MILITARY_NEC3D_LIBRARY_URL) return;

  const views = {
    perspective: { yaw: -0.72, pitch: -0.44 },
    front: { yaw: 0, pitch: -Math.PI / 2 },
    side: { yaw: Math.PI / 2, pitch: -Math.PI / 2 },
    top: { yaw: 0, pitch: 0 },
  };

  const decode = value => {
    const raw = atob(value);
    const output = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
    return output;
  };

  const colour = relative => {
    const t = Math.max(0, Math.min(1, (relative + 30) / 30));
    const stops = [[0,[89,36,190]],[.25,[39,91,213]],[.48,[21,174,189]],[.68,[82,196,105]],[.84,[239,201,63]],[1,[246,94,36]]];
    for (let index = 0; index < stops.length - 1; index += 1) {
      const [a, first] = stops[index];
      const [b, second] = stops[index + 1];
      if (t <= b) {
        const amount = (t - a) / (b - a);
        return `rgb(${first.map((value, channel) => Math.round(value + (second[channel] - value) * amount)).join(',')})`;
      }
    }
    return 'rgb(246,94,36)';
  };

  const valueAt = (dataset, thetaIndex, phiIndex) => dataset.valueOrder === 'theta-major-phi-minor'
    ? dataset.values[thetaIndex * dataset.phiCount + phiIndex]
    : dataset.values[phiIndex * dataset.thetaCount + thetaIndex];

  const loadLibrary = (() => {
    let request;
    return () => {
      if (!request) request = fetch(window.CHA_MILITARY_NEC3D_LIBRARY_URL, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        });
      return request;
    };
  })();

  const createViewer = (root, library) => {
    const canvas = root.querySelector('[data-nec3d-embed-canvas]');
    const status = root.querySelector('[data-nec3d-embed-status]');
    const modelId = root.dataset.nec3dModel;
    const config = library.configs.find(item => item.modelId === modelId);
    if (!canvas || !config) throw new Error(`Approved model not found: ${modelId}`);
    const dataset = library.datasets[config.datasetId];
    if (!dataset.values) dataset.values = decode(dataset.valuesBase64);

    const state = {
      yaw: views.perspective.yaw,
      pitch: views.perspective.pitch,
      zoom: 0.9,
      frame: 0,
      pointers: new Map(),
      lastPinch: 0,
      referenceGrid: true,
    };

    const projectCartesian = (x, y, z, width, height, db = 0) => {
      const cosineYaw = Math.cos(state.yaw), sineYaw = Math.sin(state.yaw);
      [x, y] = [x * cosineYaw - y * sineYaw, x * sineYaw + y * cosineYaw];
      const cosinePitch = Math.cos(state.pitch), sinePitch = Math.sin(state.pitch);
      [y, z] = [y * cosinePitch - z * sinePitch, y * sinePitch + z * cosinePitch];
      const scale = Math.min(width, height) * 0.39 * state.zoom;
      return { x: width / 2 + x * scale, y: height * 0.54 - y * scale, z, db };
    };

    const project = (theta, phi, db, width, height) => {
      const radius = Math.max(0.035, 10 ** (Math.max(-30, db) / 20));
      return projectCartesian(
        radius * Math.sin(theta) * Math.cos(phi),
        radius * Math.sin(theta) * Math.sin(phi),
        radius * Math.cos(theta),
        width,
        height,
        db
      );
    };

    const drawRelativeLevelGrid = (context, width, height) => {
      const levels = [0, -3, -6, -10, -20, -30];
      const radiusFor = db => Math.max(0.035, 10 ** (db / 20));
      context.save();
      context.lineWidth = 1;
      for (let spoke = 0; spoke < 12; spoke += 1) {
        const angle = spoke / 12 * Math.PI * 2;
        const centre = projectCartesian(0, 0, 0, width, height);
        const outer = projectCartesian(Math.cos(angle), Math.sin(angle), 0, width, height);
        context.beginPath();
        context.moveTo(centre.x, centre.y);
        context.lineTo(outer.x, outer.y);
        context.strokeStyle = spoke % 3 === 0 ? 'rgba(54,45,37,.30)' : 'rgba(54,45,37,.16)';
        context.stroke();
      }
      levels.forEach((db, levelIndex) => {
        const radius = radiusFor(db);
        context.beginPath();
        for (let index = 0; index <= 120; index += 1) {
          const angle = index / 120 * Math.PI * 2;
          const point = projectCartesian(radius * Math.cos(angle), radius * Math.sin(angle), 0, width, height);
          if (index) context.lineTo(point.x, point.y);
          else context.moveTo(point.x, point.y);
        }
        context.closePath();
        context.strokeStyle = levelIndex === 0 ? 'rgba(54,45,37,.42)' : 'rgba(54,45,37,.27)';
        context.setLineDash(levelIndex === 0 ? [] : [4,4]);
        context.stroke();
      });
      context.setLineDash([]);
      context.restore();
    };

    const drawRelativeLevelLabels = (context, width, height) => {
      const levels = [0, -3, -6, -10, -20, -30];
      const radiusFor = db => Math.max(0.035, 10 ** (db / 20));
      const offsets = [{x:8,y:-9},{x:8,y:-7},{x:8,y:-6},{x:8,y:-5},{x:14,y:-16},{x:17,y:10}];
      context.save();
      context.textAlign = 'left';
      context.textBaseline = 'middle';
      context.font = '900 11px system-ui,sans-serif';
      levels.forEach((db, index) => {
        const point = projectCartesian(0, radiusFor(db), 0, width, height);
        const text = (db === 0 ? '0' : '−' + Math.abs(db)) + ' dB';
        const labelX = point.x + offsets[index].x;
        const labelY = point.y + offsets[index].y;
        context.beginPath();
        context.moveTo(point.x, point.y);
        context.lineTo(labelX - 2, labelY);
        context.strokeStyle = 'rgba(54,45,37,.42)';
        context.lineWidth = 1;
        context.stroke();
        context.fillStyle = 'rgba(54,45,37,.58)';
        context.fillText(text, labelX, labelY);
      });
      context.restore();
    };

    const drawAxes = (context, width, height) => {
      const ring = [];
      for (let index = 0; index <= 96; index += 1) {
        const angle = index / 96 * Math.PI * 2;
        ring.push(projectCartesian(Math.cos(angle), Math.sin(angle), 0, width, height));
      }
      context.save();
      context.strokeStyle = 'rgba(54,45,37,.24)';
      context.setLineDash([5,6]);
      context.lineWidth = 1;
      context.beginPath();
      ring.forEach((point,index) => index ? context.lineTo(point.x,point.y) : context.moveTo(point.x,point.y));
      context.stroke();
      context.setLineDash([]);
      const centre = projectCartesian(0,0,0,width,height);
      const axes = [
        { name:'X', colour:'#d92532', negative:[-1.17,0,0], positive:[1.17,0,0] },
        { name:'Y', colour:'#16865a', negative:[0,-1.17,0], positive:[0,1.17,0] },
        { name:'Z', colour:'#2667c9', negative:[0,0,-1.17], positive:[0,0,1.17] },
      ];
      const label = (value, point, colourValue) => {
        context.save();
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.font = '900 18px system-ui,sans-serif';
        context.lineWidth = 7;
        context.strokeStyle = 'rgba(255,255,255,.98)';
        context.strokeText(value,point.x,point.y);
        context.fillStyle = colourValue;
        context.fillText(value,point.x,point.y);
        context.restore();
      };
      const arrow = (from,to,colourValue) => {
        const angle = Math.atan2(to.y-from.y,to.x-from.x), size = 11;
        context.save();
        context.fillStyle = colourValue;
        context.beginPath();
        context.moveTo(to.x,to.y);
        context.lineTo(to.x-size*Math.cos(angle-.48),to.y-size*Math.sin(angle-.48));
        context.lineTo(to.x-size*Math.cos(angle+.48),to.y-size*Math.sin(angle+.48));
        context.closePath();
        context.fill();
        context.restore();
      };
      axes.forEach(axis => {
        const negative = projectCartesian(...axis.negative,width,height);
        const positive = projectCartesian(...axis.positive,width,height);
        if (Math.hypot(positive.x-negative.x,positive.y-negative.y) < 28) {
          context.save();
          context.strokeStyle = axis.colour;
          context.lineWidth = 3;
          context.beginPath();
          context.arc(centre.x,centre.y,8,0,Math.PI*2);
          context.stroke();
          context.restore();
          label(`+${axis.name}${axis.name==='Z'?' · UP':''}`,{x:centre.x+78,y:centre.y-21},axis.colour);
          label(`−${axis.name}`,{x:centre.x+78,y:centre.y+14},axis.colour);
          return;
        }
        context.save();
        context.strokeStyle = axis.colour;
        context.lineWidth = 2.6;
        context.lineCap = 'round';
        context.setLineDash([8,7]);
        context.beginPath();
        context.moveTo(negative.x,negative.y);
        context.lineTo(centre.x,centre.y);
        context.stroke();
        context.setLineDash([]);
        context.beginPath();
        context.moveTo(centre.x,centre.y);
        context.lineTo(positive.x,positive.y);
        context.stroke();
        context.restore();
        arrow(centre,positive,axis.colour);
        const offset = (point,origin,amount=18) => {
          const dx=point.x-origin.x, dy=point.y-origin.y, distance=Math.max(1,Math.hypot(dx,dy));
          return {x:point.x+dx/distance*amount,y:point.y+dy/distance*amount};
        };
        label(`+${axis.name}${axis.name==='Z'?' · UP':''}`,offset(positive,centre),axis.colour);
        label(`−${axis.name}`,offset(negative,centre),axis.colour);
      });
      context.beginPath();
      context.arc(centre.x,centre.y,5,0,Math.PI*2);
      context.fillStyle = '#362d25';
      context.fill();
      // The origin point is sufficient; text here obscures the radiation volume.
      context.restore();
    };

    const drawPatternNotice = (context, width) => {
      context.save();
      context.fillStyle = 'rgba(255,255,255,.96)';
      context.strokeStyle = '#d8d0c2';
      context.lineWidth = 1;
      context.fillRect(14, 14, Math.min(width - 28, 360), 48);
      context.strokeRect(14, 14, Math.min(width - 28, 360), 48);
      context.fillStyle = '#17130f';
      context.textAlign = 'left';
      context.font = '900 12px system-ui,sans-serif';
      context.fillText('NORMALIZED DIRECTIONAL PATTERN', 26, 34);
      context.fillStyle = '#61594f';
      context.font = '700 11px system-ui,sans-serif';
      context.fillText('Not physical size, distance or visible RF', 26, 51);
      context.restore();
    };

    const render = () => {
      cancelAnimationFrame(state.frame);
      state.frame = requestAnimationFrame(() => {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const ratio = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.max(480, Math.round(rect.width * ratio));
        canvas.height = Math.max(360, Math.round(rect.height * ratio));
        const context = canvas.getContext('2d');
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        const width = canvas.width / ratio, height = canvas.height / ratio;
        const gradient = context.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#ffffff');
        gradient.addColorStop(1, '#f4f0e7');
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);

        const thetaStride = Math.max(1, Math.ceil(dataset.thetaCount / 92));
        const thetaStep = dataset.thetaStepDegrees * Math.PI / 180;
        const phiStep = dataset.phiStepDegrees * Math.PI / 180;
        const thetaStart = (dataset.thetaStartDegrees || 0) * Math.PI / 180;
        const phiStart = (dataset.phiStartDegrees || 0) * Math.PI / 180;
        const points = [];
        for (let thetaIndex = 0; thetaIndex < dataset.thetaCount; thetaIndex += thetaStride) {
          const row = [];
          for (let phiIndex = 0; phiIndex < dataset.phiCount; phiIndex += 1) {
            const encoded = valueAt(dataset, thetaIndex, phiIndex);
            const db = -30 + encoded / 255 * 30;
            row.push(project(thetaStart + thetaIndex * thetaStep, phiStart + phiIndex * phiStep, db, width, height));
          }
          points.push(row);
        }

        const cells = [];
        for (let rowIndex = 0; rowIndex < points.length - 1; rowIndex += 1) {
          for (let columnIndex = 0; columnIndex < dataset.phiCount; columnIndex += 1) {
            const nextColumn = (columnIndex + 1) % dataset.phiCount;
            const polygon = [points[rowIndex][columnIndex], points[rowIndex + 1][columnIndex], points[rowIndex + 1][nextColumn], points[rowIndex][nextColumn]];
            cells.push({
              polygon,
              z: polygon.reduce((sum, point) => sum + point.z, 0) / 4,
              db: polygon.reduce((sum, point) => sum + point.db, 0) / 4,
            });
          }
        }
        if (state.referenceGrid) drawRelativeLevelGrid(context, width, height);
        cells.sort((first, second) => first.z - second.z).forEach(cell => {
          context.beginPath();
          cell.polygon.forEach((point, index) => index ? context.lineTo(point.x, point.y) : context.moveTo(point.x, point.y));
          context.closePath();
          context.globalAlpha = 0.88;
          context.fillStyle = colour(cell.db);
          context.fill();
          context.globalAlpha = 0.16;
          context.strokeStyle = '#362d25';
          context.lineWidth = 0.55;
          context.stroke();
        });
        context.globalAlpha = 1;
        drawAxes(context, width, height);
        if (state.referenceGrid) drawRelativeLevelLabels(context, width, height);
        drawPatternNotice(context, width);
      });
    };

    const setPreset = name => {
      const preset = views[name] || views.perspective;
      state.yaw = preset.yaw;
      state.pitch = preset.pitch;
      root.querySelectorAll('[data-nec3d-embed-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.nec3dEmbedView === name)));
      render();
    };

    const endPointer = event => {
      state.pointers.delete(event.pointerId);
      state.lastPinch = 0;
      root.classList.toggle('is-dragging', state.pointers.size > 0);
    };

    canvas.addEventListener('pointerdown', event => {
      event.preventDefault();
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      root.classList.add('is-dragging');
      try { canvas.setPointerCapture(event.pointerId); } catch (error) { /* Brave may decline capture during an iframe transition. */ }
    });
    canvas.addEventListener('pointermove', event => {
      if (!state.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const previous = state.pointers.get(event.pointerId);
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const active = Array.from(state.pointers.values());
      if (active.length === 2) {
        const distance = Math.hypot(active[0].x - active[1].x, active[0].y - active[1].y);
        if (state.lastPinch) state.zoom = Math.max(0.45, Math.min(1.8, state.zoom * distance / state.lastPinch));
        state.lastPinch = distance;
      } else {
        state.yaw += (event.clientX - previous.x) * 0.009;
        state.pitch = Math.max(-1.45, Math.min(1.45, state.pitch + (event.clientY - previous.y) * 0.009));
      }
      render();
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => canvas.addEventListener(type, endPointer));
    window.addEventListener('pointerup', endPointer, { passive: true });
    canvas.addEventListener('wheel', event => {
      event.preventDefault();
      state.zoom = Math.max(0.45, Math.min(1.8, state.zoom * (event.deltaY > 0 ? 0.92 : 1.08)));
      render();
    }, { passive: false });
    canvas.addEventListener('keydown', event => {
      const handled = ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','+','=','-'].includes(event.key);
      if (!handled) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') state.yaw -= 0.12;
      if (event.key === 'ArrowRight') state.yaw += 0.12;
      if (event.key === 'ArrowUp') state.pitch = Math.max(-1.45, state.pitch - 0.12);
      if (event.key === 'ArrowDown') state.pitch = Math.min(1.45, state.pitch + 0.12);
      if (event.key === '+' || event.key === '=') state.zoom = Math.min(1.8, state.zoom * 1.08);
      if (event.key === '-') state.zoom = Math.max(0.45, state.zoom * 0.92);
      render();
    });
    root.addEventListener('click', event => {
      const viewButton = event.target.closest('[data-nec3d-embed-view]');
      if (viewButton) setPreset(viewButton.dataset.nec3dEmbedView);
      const gridButton = event.target.closest('[data-nec3d-embed-grid]');
      if (gridButton) {
        state.referenceGrid = !state.referenceGrid;
        gridButton.setAttribute('aria-pressed', String(state.referenceGrid));
        status.textContent = state.referenceGrid
          ? 'Pattern grid shown — ring labels are normalized relative level in dB'
          : 'Interactive pattern ready — X/Y/Z are model-relative axes';
        render();
      }
      if (event.target.closest('[data-nec3d-embed-reset]')) {
        state.zoom = 0.9;
        setPreset('perspective');
      }
    });

    const observer = 'ResizeObserver' in window ? new ResizeObserver(render) : null;
    if (observer) observer.observe(canvas);
    else window.addEventListener('resize', render, { passive: true });
    root.classList.add('is-ready');
    status.textContent = 'Pattern grid shown — ring labels are normalized relative level in dB';
    setPreset('perspective');
  };

  loadLibrary()
    .then(library => roots.forEach(root => {
      try { createViewer(root, library); }
      catch (error) {
        const status = root.querySelector('[data-nec3d-embed-status]');
        if (status) status.textContent = 'Static fallback shown — open the complete laboratory';
      }
    }))
    .catch(() => roots.forEach(root => {
      const status = root.querySelector('[data-nec3d-embed-status]');
      if (status) status.textContent = 'Static fallback shown — interactive data unavailable';
    }));
})();
